/**
 * Generic event queue for handling events before collectors are initialized
 */

export interface QueuedEvent<T = any> {
  data: T;
  timestamp: number;
  retryCount: number;
}

export interface EventProcessor<T> {
  process(data: T): void;
  isReady(): boolean;
}

export class EventQueue<T = any> {
  private queue: QueuedEvent<T>[] = [];
  private processor: EventProcessor<T> | null = null;
  private flushInterval: NodeJS.Timeout | null = null;
  private isFlushScheduled = false;
  private readonly maxQueueSize: number;
  private readonly maxAge: number; // in milliseconds
  private readonly flushIntervalMs: number;
  private readonly name: string;

  constructor(options: {
    name: string;
    maxQueueSize?: number;
    maxAge?: number; // in milliseconds
    flushIntervalMs?: number;
  }) {
    this.name = options.name;
    this.maxQueueSize = options.maxQueueSize || 50;
    this.maxAge = options.maxAge || 5 * 60 * 1000; // 5 minutes default
    this.flushIntervalMs = options.flushIntervalMs || 1000; // 1 second default

    this.startPeriodicFlush();
  }

  /**
   * Set the processor that will handle queued events
   */
  setProcessor(processor: EventProcessor<T>): void {
    this.processor = processor;
    // Try to flush immediately when processor is set
    this.scheduleFlush();
  }

  /**
   * Add an event to the queue or process immediately if processor is ready
   */
  enqueue(data: T): boolean {
    try {
      // If processor is ready, process immediately
      if (this.processor?.isReady()) {
        this.processor.process(data);
        // Also flush any queued events since processor is now ready
        if (this.queue.length > 0) {
          this.flushQueue();
        }
        return true;
      }

      // Processor not ready, queue the event
      if (this.queue.length >= this.maxQueueSize) {
        console.warn(`[${this.name}] Queue is full, dropping event`);
        return false;
      }

      this.queue.push({
        data,
        timestamp: Date.now(),
        retryCount: 0
      });

      console.warn(`[${this.name}] Processor not ready, event queued (${this.queue.length} in queue)`);
      this.scheduleFlush();
      return true;
    } catch (error) {
      console.error(`[${this.name}] Error enqueuing event:`, error);
      return false;
    }
  }

  /**
   * Get current queue size
   */
  size(): number {
    return this.queue.length;
  }

  /**
   * Clear all queued events
   */
  clear(): void {
    this.queue.length = 0;
  }

  /**
   * Destroy the queue and clean up resources
   */
  destroy(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.clear();
    this.processor = null;
  }

  /**
   * Schedule a flush attempt for the next tick
   */
  private scheduleFlush(): void {
    if (!this.isFlushScheduled) {
      this.isFlushScheduled = true;
      setTimeout(() => {
        this.isFlushScheduled = false;
        this.flushQueue();
      }, 0);
    }
  }

  /**
   * Flush queued events when processor becomes available
   */
  private flushQueue(): void {
    if (!this.processor?.isReady() || this.queue.length === 0) {
      return;
    }

    const eventsToProcess = [...this.queue];
    this.queue.length = 0; // Clear the queue

    let processedCount = 0;
    eventsToProcess.forEach(({ data }) => {
      try {
        this.processor!.process(data);
        processedCount++;
      } catch (error) {
        console.error(`[${this.name}] Error flushing queued event:`, error);
      }
    });

    if (processedCount > 0) {
      console.log(`[${this.name}] Flushed ${processedCount} queued events`);
    }
  }

  /**
   * Start periodic flushing and cleanup
   */
  private startPeriodicFlush(): void {
    this.flushInterval = setInterval(() => {
      // Try to flush if processor is ready
      if (this.processor?.isReady() && this.queue.length > 0) {
        this.flushQueue();
      }

      // Clean up old events to prevent memory leaks
      this.cleanupOldEvents();
    }, this.flushIntervalMs);

    // Clean up interval in Node.js environment (for tests)
    if (typeof window === 'undefined' && typeof process !== 'undefined') {
      process.on('exit', () => this.destroy());
    }
  }

  /**
   * Remove events older than maxAge
   */
  private cleanupOldEvents(): void {
    const cutoffTime = Date.now() - this.maxAge;
    const originalLength = this.queue.length;

    this.queue = this.queue.filter((event) => event.timestamp > cutoffTime);

    if (this.queue.length < originalLength) {
      const removedCount = originalLength - this.queue.length;
      console.warn(`[${this.name}] Removed ${removedCount} stale events from queue`);
    }
  }
}
